/* 
  This page is shown when the login request is sent from OSD and user is successfully authorized.
  When user press the "open in desktop app", a special protocol will be open with two params: app_auth_id and authCode
  app_auth_id is for OSD to make sure the auth request is authenticated
  authCode (keycloak refresh token) is sent for OWD can update token internally
*/
import { Button, H2, Grid } from '@ouster/webviz-react-components';

import { useLocation } from 'react-router-dom';

import { useKeycloak } from '../../KeycloakContext';
import { useStoreState } from '../../reducers';

const isSafari = () =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export default function AuthRedirect() {
  const { user } = useStoreState((storeState) => ({
    ...storeState.user,
  }));
  const { keycloak } = useKeycloak();
  const location = useLocation();

  const sendAuthViaPort = async (appAuthId: string, authCode: string) => {
    const postUrl = 'http://localhost:54321/auth';
    try {
      const response = await fetch(postUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app_auth_id: appAuthId, code: authCode }),
      });

      if (response.ok) {
        return;
      }

      console.warn('POST auth handoff failed; trying legacy fallback.');
    } catch {
      console.warn('POST auth handoff error; trying legacy fallback.');
    }

    // Legacy desktop builds may parse query params without decoding.
    // Keep raw values here to preserve historical compatibility.
    const legacyUrl = `http://localhost:54321/auth?app_auth_id=${appAuthId}&code=${authCode}`;

    fetch(legacyUrl, { method: 'GET' })
      .then((response) => {
        if (response.ok) {
          return;
        }
        console.warn('Legacy auth handoff did not complete successfully.');
      })
      .catch(() => {
        console.warn('Legacy auth handoff failed.');
      });
  };

  const notifyApp = () => {
    const params = new URLSearchParams(location.search);
    const appAuthId = params.get('app_auth_id');

    // Validate app_auth_id is a non-empty alphanumeric/dash/underscore string
    // to prevent open-redirect abuse (VULN-09).
    if (!appAuthId || !/^[\w-]{1,128}$/.test(appAuthId)) {
      console.error('Invalid app_auth_id; aborting desktop auth handoff.');
      return;
    }

    const authCode = keycloak?.refreshToken;

    const appAuthProtocol = sessionStorage.getItem('appAuthProtocol');

    if (appAuthId && authCode) {
      /* 
        Safari doesn't allow call http from https.
        Hence, for safari browser, we will use custom portal to send auth code
      */
      if (appAuthProtocol === 'port' && !isSafari()) {
        /* 
          OSD will indicate the the protocol for sending auth code, it is implemented since release 5.
          Try POST first for better token hygiene; if desktop is old and does
          not support POST yet, fall back to legacy GET for compatibility.
        */
        sendAuthViaPort(appAuthId, authCode).catch(() => {
          console.warn('Auth handoff failed.');
        });
      } else {
        /* 
          Backward support, in previous osd, we send auth using custom protocol.
          The refresh token is embedded in a custom-protocol URL; no practical
          alternative exists for the custom-protocol path without an OSD update.
        */
        window.location.href = `osd://auth?app_auth_id=${appAuthId}&code=${authCode}`;
      }

      sessionStorage.removeItem('appAuthProtocol');
      sessionStorage.removeItem('appAuthId');
    }
  };

  if (!user) return null;
  return (
    <Grid placeContent="center" rowGap="2rem" h="100%" minW={400}>
      <H2 textAlign="center">
        Log in as
        <br />
        {user?.email}
      </H2>
      <Button onClick={notifyApp}>Open in desktop app</Button>
    </Grid>
  );
}
